import React, { useState } from 'react';
import Popup from '@ajat/design-system/Popup';
import Button from '@ajat/design-system/Button';
import { ComponentPage, DemoCard } from '../../shared';

const Stage = ({ mobile = false, children }) => (
  <div
    className={`${mobile ? 'ds-popup-mobile-preview' : 'ds-popup-preview flex items-center justify-center'} bg-[var(--color-gray-025)] p-[var(--space-ui-large-s)] rounded-[var(--radius-card)] border border-[var(--color-gray-1)]`}
  >
    {children}
  </div>
);

export const meta = {
  component: Popup,
  title: 'Popup',
  group: 'Overlays',
  importPath: '@ajat/design-system/Popup',
  figmaNode: '3969:1982',
  description:
    'Confirmation dialog. A centred modal on desktop, a bottom sheet with swipe-to-dismiss on mobile. Body scroll is locked automatically while it is open — except in preview mode, which is how the static examples below can sit inline.',
  props: [
    ['isOpen', 'bool', 'false', 'Open state'],
    ['onClose', 'func', '—', 'Fires on overlay click, the X button, Escape and swipe-down'],
    ['onConfirm', 'func', '—', 'Confirm button handler'],
    ['onCancel', 'func', '—', 'Cancel button handler'],
    ['title', 'string', "'Title'", 'Heading (typo-h2)'],
    ['infoText', 'string', "''", 'Info block on a gray-05 ground'],
    ['infoVariant', "'success' | 'warning' | 'error'", "'success'", 'Info block colours — the ui-* token pairs (warning for destructive confirms)'],
    ['showInfo', 'bool', 'true', 'Show the info block'],
    ['message', 'string', "''", 'Body copy'],
    ['showMessage', 'bool', 'true', 'Show the body copy'],
    ['cancelLabel', 'string', "'Cancel'", 'Cancel button label'],
    ['confirmLabel', 'string', "'Confirm'", 'Confirm button label'],
    ['showCancel', 'bool', 'true', 'Show the cancel button'],
    ['confirmVariant', "'black' | 'danger' | 'main' | 'outline'", "'black'", 'Confirm button variant'],
    ['confirmDisabled', 'bool', 'false', 'Disables the confirm button'],
    ['preview', 'bool', 'false', 'Static mode — no overlay, no portal, no scroll lock'],
    ['lockScroll', 'bool', 'true', 'Lock body scroll while open'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['children', 'node', '—', 'Custom body, replacing info and message'],
  ],
  usage: [
    {
      title: 'Destructive confirmation',
      code: `import Popup from '@ajat/design-system/Popup';

<Popup
  isOpen={Boolean(removeTarget)}
  onClose={() => setRemoveTarget(null)}
  onCancel={() => setRemoveTarget(null)}
  onConfirm={confirmRemove}
  title="Fjern produkt?"
  message="Denne handling kan ikke fortrydes."
  confirmLabel="Fjern"
  cancelLabel="Annuller"
  confirmVariant="danger"
/>`,
    },
  ],
};

export default function PopupPage() {
  const [open, setOpen] = useState(false);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live">
        <Button size="primary" variant="main" onClick={() => setOpen(true)}>Open Popup</Button>
        <Popup
          isOpen={open}
          onClose={() => setOpen(false)}
          onCancel={() => setOpen(false)}
          onConfirm={() => setOpen(false)}
          title="Fjern produkt?"
          infoText="Produktet fjernes fra din kurv med det samme."
          message="Denne handling kan ikke fortrydes."
          confirmLabel="Fjern"
          cancelLabel="Annuller"
          confirmVariant="danger"
        />
      </DemoCard>

      <DemoCard title="Desktop — default" surface={false}>
        <Stage>
          <Popup
            isOpen
            preview
            onClose={() => {}}
            title="Fjern produkt?"
            infoText="Produktet fjernes fra din kurv med det samme."
            message="Denne handling kan ikke fortrydes."
            confirmLabel="Fjern"
            cancelLabel="Annuller"
          />
        </Stage>
      </DemoCard>

      <DemoCard title="Desktop — danger confirm" surface={false}>
        <Stage>
          <Popup
            isOpen
            preview
            onClose={() => {}}
            title="Fjern produkt?"
            message="Denne handling kan ikke fortrydes."
            showInfo={false}
            confirmLabel="Fjern"
            cancelLabel="Annuller"
            confirmVariant="danger"
          />
        </Stage>
      </DemoCard>

      <DemoCard title="Desktop — single action" surface={false}>
        <Stage>
          <Popup
            isOpen
            preview
            onClose={() => {}}
            title="Success"
            message="Your order has been updated."
            showInfo={false}
            showCancel={false}
            confirmLabel="OK"
          />
        </Stage>
      </DemoCard>

      <DemoCard title="Desktop — disabled confirm" surface={false}>
        <Stage>
          <Popup
            isOpen
            preview
            onClose={() => {}}
            title="Confirm action"
            message="Tick the box above to continue."
            showInfo={false}
            confirmDisabled
          />
        </Stage>
      </DemoCard>

      <DemoCard title="Mobile — bottom sheet" surface={false}>
        <Stage mobile>
          <Popup
            isOpen
            preview
            onClose={() => {}}
            title="Fjern produkt?"
            infoText="Produktet fjernes fra din kurv med det samme."
            message="Denne handling kan ikke fortrydes."
            confirmLabel="Fjern"
            cancelLabel="Annuller"
            confirmVariant="danger"
          />
        </Stage>
      </DemoCard>
    </ComponentPage>
  );
}
